import { Spine } from 'pixi-spine'
import { Loader } from 'pixi.js'
import { signal } from 'services/SignalsService'

import { delayCallBack } from '../../utils/Delay'
import { enableSpineResize } from '../handlers/ResizeHandler'
import SpineButton from './SpineButton'
import SpineInclude from './SpineInclude'

export default class _Spine extends Spine {

  constructor({ spine, speed, width, height, resize, buttons, buttonSignal, include, includeSlot, includeType, unpressed, pressed }) {
    super(Loader.shared.resources[spine].spineData)
    this._timeScale = 1

    this._speed = speed

    this.x = (width || 0) / 2
    this.y = (height || 0) / 2

    this._animated = false

    this.buttons = {}
    this.includes = {}

    this.createButtons(buttons, buttonSignal, spine, unpressed, pressed)

    signal.once('INCLUDE:INIT', () => {
      this.createIncludes(include, includeSlot, includeType)
    })

    if (resize) {
      enableSpineResize(this)
    }

    this.createSignal()
    this.stop()
  }

  createIncludes(_includes, _slots, _types) {
    if (!_includes) {
      return
    }

    if (!this.state.hasAnimation('0')) {
      throw new Error('SpineInclude doesnt have animation 0')
    }
    this._animated = true
    this.skeleton.setToSetupPose()
    this.state.setAnimation(0, '0', true)

    const elements = _includes.split(',')
    const slots = _slots.split(',')
    const types = _types.split(',')

    elements.forEach((name, index) => {
      const config = {
        spine: this,
        include: name,
        slot: slots[index],
        type: types[index],
        name,
        index,
      }
      this.includes[name] = new SpineInclude(config)
    })
  }

  createButtons(buttons, buttonSignal, spine, unpressed, pressed){
    if (buttons) {
      const elements = buttons.split(',')
      const dispatch =  buttonSignal ? buttonSignal.split(',') : null
      elements.forEach((key, index) => {
        const signalDispatch = buttonSignal ? dispatch[index] : null
        const buttonName = (typeof buttons[key] === 'object') ? key.name : key
        if (!this.skeleton.findSlot(buttonName)) {
          throw new Error(`Obj ${spine} has no slot ${buttonName}`)
        }
        this.buttons[index] = new SpineButton({
          index: index,
          name: buttonName,
          parent: this,
          sprite: this.getSpineChildByName(buttonName),
          bone: this.skeleton.findSlot(buttonName).bone,
          buttonOptions: buttons[key],
          signal: signalDispatch, unpressed: unpressed || 1, pressed: pressed || 0.8,
        })
      })
    }
  }

  enableButton(interactive){
    for (const key in this.buttons) {
      this.buttons[key].interactivity = interactive
    }
  }

  createSignal(){
    signal.on('ANIMATION:TIME-SCALE', this.setTimeScale.bind(this))
  }

  goto(name = '', time = Number.POSITIVE_INFINITY) {
    this._animated = false
    name = this.state.hasAnimation(name) ? name : 'animation'
    const track = this.state.setAnimation(0, name, false)
    this.update(Math.min(time, track.trackEnd))
  }

  play(name = '', loop = true) {
    this._animated = true
    name = this.state.hasAnimation(name) ? name : 'animation'
    const track = this.state.setAnimation(0, name, loop)
    track.timeScale = this._speed || 1
    return delayCallBack(track.animationEnd / track.timeScale)
  }

  playCallBack(animationName, loop = false, cb) {
    if (!this.state.hasAnimation(animationName)) {
      throw new Error(`There is no animation with name: ${animationName}`)
    }

    this._animated = true
    this.anim = this.state.setAnimation(0, animationName, loop)

    if (cb) {
      this.state.tracks[0].listener = {
        complete: () => {
          cb()
        },
      }
    }
  }

  stop() {
    this._animated = false
    this.state.clearTracks()
    this.update(0)
  }

  setToSetupPose(){
    this.skeleton.setToSetupPose()
  }

  setTimeScale(value) {
    this._timeScale = value
    this.state.timeScale = this._timeScale
  }

  getSpineChildByName(name) {
    return this.children[this.skeleton.findSlotIndex(name)]
  }

  getSpineSlotContainersName(name) {
    return this.slotContainers[this.skeleton.findSlotIndex(name)]
  }

  getSpineSlotIndexByName(name) {
    return this.skeleton.findSlotIndex(name)
  }

  getSpineSlotByName(name) {
    return this.skeleton.findSlot(name)
  }

  getSpineBoneByName(name) {
    return this.skeleton.findBone(name)
  }

  addAttachment(config) {
    this.skeleton.setAttachment(config.slot, config.sprite)
  }

  getAttachment(slot, sprite) {
    return this.skeleton.getAttachmentByName(slot, sprite)
  }

  setInteractivityChild(value){
    if(this.buttons){
      for (let prop in this.buttons) {
        this.buttons[prop].interactivity = value
      }
    }
  }
}
